import { RightWrongButtons } from '@components';
import { useScreenSize } from '@hooks';
import { Flex } from '@styles';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import React, { useMemo, useState } from 'react';

import { TestFinishCard } from '../TestFinishCard';
import Card from './TestCard';
import { FrameContainer } from './TestCard.layoutProps';

const TestCardsFrame = ({
  correctAnswers,
  customButtonTitles,
  data,
  handleGoBack,
  handleReset,
  handleShowAnswers,
  isTestFinished,
  isTestPassed,
  isValidated,
  onLeftButtonPress,
  onRightButtonPress,
  withCornerNumbers,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [skippedCards, setSkippedCards] = useState([]);
  const [areButtonsBlocked, setAreButtonsBlocked] = useState(false);
  const { device, dynamicWidth } = useScreenSize();

  const memoCards = useMemo(
    () =>
      data?.map((item, index) => (
        <Card
          key={index}
          isActive={activeIndex === index}
          isSkipped={skippedCards.includes(index)}
          questionCount={data?.length}
          questionNumber={index + 1}
          text={item.question}
          withCornerNumbers={withCornerNumbers}
          zIndex={-(index + 1)}
        />
      )),
    [data, skippedCards, activeIndex]
  );

  const blockButtons = () => {
    setAreButtonsBlocked(true);
    setTimeout(() => {
      setAreButtonsBlocked(false);
    }, 100);
  };

  const onSkip = () => {
    setActiveIndex((prevIndex) => {
      const newIndex = (prevIndex + 1) % data.length;
      setSkippedCards((prev) =>
        prev?.includes(prevIndex) ? prev : [...prev, prevIndex]
      );
      return newIndex;
    });
  };

  const handleLeftButton = () => {
    blockButtons();
    onSkip();
    onLeftButtonPress?.();
  };

  const handleRightButton = () => {
    blockButtons();
    onSkip();
    onRightButtonPress?.();
  };

  const onReset = () => {
    setActiveIndex(0);
    setSkippedCards([]);
    handleReset();
  };

  return (
    <Flex flexDirection="column" mt={24}>
      {isTestFinished && isValidated ? (
        <TestFinishCard
          correctAnswers={correctAnswers}
          handleShowAnswers={handleShowAnswers}
          isPassed={isTestPassed}
          isValidated
          totalAnswers={data?.length}
        />
      ) : (
        <FrameContainer device={device} dynamicWidth={dynamicWidth}>
          {memoCards?.map((el) => el)}
        </FrameContainer>
      )}
      <RightWrongButtons
        areButtonsBlocked={areButtonsBlocked}
        customButtonTitles={customButtonTitles}
        isPassed={isTestFinished && isTestPassed}
        rightHandler={isTestFinished ? handleGoBack : handleRightButton}
        withRetry={isTestFinished}
        wrongHandler={isTestFinished ? onReset : handleLeftButton}
      />
    </Flex>
  );
};

TestCardsFrame.propTypes = {
  correctAnswers: number.isRequired,
  customButtonTitles: shape({ leftButton: string, rightButton: string }),
  data: arrayOf(object).isRequired,
  handleGoBack: func.isRequired,
  handleReset: func.isRequired,
  handleShowAnswers: func.isRequired,
  isTestFinished: bool.isRequired,
  isTestPassed: bool.isRequired,
  isValidated: bool.isRequired,
  onLeftButtonPress: func.isRequired,
  onRightButtonPress: func.isRequired,
  withCornerNumbers: bool,
};

TestCardsFrame.defaultProps = {
  customButtonTitles: null,
  withCornerNumbers: false,
};

export default TestCardsFrame;
